<template>
	<div class="ark-listbox-dropdown">
		<div v-if="label" class="label">
			{{ label }}
		</div>

		<Popover.Root
			v-model:open="isOpen"
			:positioning="{ placement: 'bottom-start', gutter: 0, sameWidth: props.sameWidth, strategy: 'fixed' }"
		>
			<Popover.Trigger as-child>
				<slot name="trigger" :display-text="displayText" :selected-count="selectedValues.length" :is-open="isOpen">
					<button
						class="trigger"
						:class="{
							'has-selection': selectedValues.length > 0,
							'is-invalid': error,
						}"
						:disabled="disabled"
					>
						<span ref="displayTextEl" class="display-text">{{ displayText }}</span>
						<div class="trigger-icons">
							<button
								v-if="clearable && selectedValues.length > 0"
								@click.stop="clearSelection"
								@mousedown.stop.prevent
								class="clear-button"
								type="button"
								:disabled="disabled"
							>
								×
							</button>
							<span class="chevron" :class="{ open: isOpen }"></span>
						</div>
					</button>
				</slot>
			</Popover.Trigger>

			<Popover.Positioner>
				<Popover.Content class="popover-content" :class="{ 'same-width': props.sameWidth }">
					<div v-if="isSearchable" class="search-container">
						<input
							v-model="searchQuery"
							type="text"
							class="search-input"
							:placeholder="searchPlaceholder"
							@click.stop
						/>
					</div>

					<Listbox.Root
						:key="listboxResetKey"
						:collection="filteredCollection"
						:model-value="selectedValues"
						@value-change="handleValueChange"
						:selectionMode="multiple ? 'multiple' : 'single'"
						class="listbox"
					>
						<div v-if="filteredCollection.items.length === 0" class="no-options">
							{{ noOptionsText }}
						</div>
						<Listbox.ItemGroup v-for="group in itemGroups" :key="group.label" class="item-group">
							<Listbox.ItemGroupLabel v-if="group.label" class="item-group-label">
								<div v-if="selectableGroups || collapsibleGroups" class="group-toggle">
									<button
										v-if="collapsibleGroups"
										type="button"
										class="group-name"
										:aria-expanded="isGroupExpanded(group.label)"
										@click="toggleGroupExpanded(group.label)"
									>
										<AppIcon
											:name="IconName.ChevronRightSmall"
											:size="10"
											class="group-chevron"
											:class="{ expanded: isGroupExpanded(group.label) }"
										/>
										<span>{{ group.label }}</span>
										<span
											v-if="!isGroupExpanded(group.label) && selectedCountInGroup(group.label) > 0"
											class="group-count"
										>
											{{ selectedCountInGroup(group.label) }}
										</span>
									</button>
									<span v-else class="group-name" @click="toggleGroup(group.label)">{{ group.label }}</span>
									<span v-if="selectableGroups" class="group-checkbox">
										<ArkCheckbox :checked="isGroupSelected(group.label)" @change="toggleGroup(group.label)" />
									</span>
								</div>
								<template v-else>{{ group.label }}</template>
							</Listbox.ItemGroupLabel>
							<Listbox.Item
								v-for="item in isGroupExpanded(group.label) ? group.items : []"
								:key="item.value"
								:item="item"
								class="listbox-item"
							>
								<Listbox.ItemText class="item-text">
									{{ item.label }}
								</Listbox.ItemText>
								<Listbox.ItemIndicator class="item-indicator">
									<span class="checkbox">✓</span>
								</Listbox.ItemIndicator>
							</Listbox.Item>
						</Listbox.ItemGroup>
					</Listbox.Root>
				</Popover.Content>
			</Popover.Positioner>
		</Popover.Root>

		<div v-if="error" class="error-message">
			{{ error }}
		</div>
	</div>
</template>

<script setup lang="ts">
import { Listbox, Popover, createListCollection } from "@ark-ui/vue"
import ArkCheckbox from "@/components/ui/ArkCheckbox.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue"
import { useI18n } from "vue-i18n"

const NULL_TOKEN = "__ARK_NULL__"
const { t } = useI18n()

interface ListboxOption {
	value: string | number | null
	label: string
	disabled?: boolean
	group?: string
}

type ListboxValue = (string | number | null)[] | string | number | null

type ListboxValueChange = {
	from: ListboxValue
	to: ListboxValue
}

interface Props {
	modelValue?: ListboxValue
	options: ListboxOption[]
	placeholder?: string
	label?: string
	disabled?: boolean
	clearable?: boolean
	multiple?: boolean
	searchable?: boolean
	searchThreshold?: number
	searchPlaceholder?: string
	noOptionsText?: string
	error?: string
	searchNormalizer?: (s: string) => string
	sameWidth?: boolean
	selectableGroups?: boolean
	collapsibleGroups?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	placeholder: "Select options",
	clearable: true,
	multiple: true,
	searchable: false,
	searchThreshold: 6,
	noOptionsText: "No options available",
	modelValue: () => [],
	sameWidth: true,
	selectableGroups: false,
	collapsibleGroups: false,
})

const emit = defineEmits<{
	"update:modelValue": [value: ListboxValue]
	change: [value: ListboxValueChange]
}>()

const isOpen = ref(false)
const searchQuery = ref("")
const listboxResetKey = ref(0)
const displayTextEl = ref<HTMLElement | null>(null)
const availableWidth = ref(0)
const canvasContext = document.createElement("canvas").getContext("2d")

let resizeObserver: ResizeObserver | undefined

onMounted(() => {
	if (!displayTextEl.value) return
	resizeObserver = new ResizeObserver(([entry]) => {
		availableWidth.value = entry?.contentRect.width ?? 0
	})
	resizeObserver.observe(displayTextEl.value)
})

onBeforeUnmount(() => {
	resizeObserver?.disconnect()
})

function measureTextWidth(text: string): number {
	if (!canvasContext || !displayTextEl.value) return text.length * 7
	canvasContext.font = getComputedStyle(displayTextEl.value).font
	return canvasContext.measureText(text).width
}
const searchPlaceholder = computed(() => props.searchPlaceholder ?? t("filters.filters-search"))
const isSearchable = computed(() => props.options.length > props.searchThreshold)

const toKey = (v: string | number | null | undefined) => (v === null || v === undefined ? NULL_TOKEN : String(v))

const normalize = (s: string) => (props.searchNormalizer ? props.searchNormalizer(s) : s.toLowerCase())

function copyValue(value: ListboxValue | undefined): ListboxValue {
	if (Array.isArray(value)) {
		return [...value]
	}

	return value ?? null
}

function areValuesEqual(left: ListboxValue | undefined, right: ListboxValue): boolean {
	if (Array.isArray(left) || Array.isArray(right)) {
		const leftValues = Array.isArray(left) ? left : left == null ? [] : [left]
		const rightValues = Array.isArray(right) ? right : right == null ? [] : [right]

		return leftValues.length === rightValues.length && leftValues.every((value, index) => value === rightValues[index])
	}

	return (left ?? null) === (right ?? null)
}

// tracks whether the next props.modelValue change originated from this component's own
// emit, so the watcher below can tell it apart from an external one (e.g. a parent-level
// "reset filters" action) that the underlying Ark listbox doesn't otherwise pick up
let isInternalModelValueUpdate = false

function updateModelValue(value: ListboxValue): void {
	if (areValuesEqual(props.modelValue, value)) {
		return
	}

	isInternalModelValueUpdate = true
	emit("change", {
		from: copyValue(props.modelValue),
		to: copyValue(value),
	})
	emit("update:modelValue", value)
}

const selectedValues = computed<string[]>({
	get: () => {
		const mv = props.modelValue as unknown

		if (props.multiple) {
			if (Array.isArray(mv)) {
				return (mv as (string | number | null)[]).map(v => toKey(v))
			}
			if (mv == null) return []
			return [toKey(mv as string | number | null)]
		} else {
			let single: string | number | null | undefined
			if (Array.isArray(mv)) {
				single = (mv as (string | number | null)[])[0] ?? null
			} else {
				single = mv as string | number | null | undefined
			}
			return single == null ? [] : [toKey(single)]
		}
	},
	set: (values: string[]) => {
		const originalValues = values
			.map(key => props.options.find(opt => toKey(opt.value) === key)?.value)
			.filter(v => v !== undefined) as (string | number | null)[]

		updateModelValue(props.multiple ? originalValues : originalValues[0] ?? null)
	},
})

// snapshot, not live, so an item doesn't jump to the top mid-selection while the user is still picking
const pinnedSelection = ref<Set<string>>(new Set(selectedValues.value))

const collection = computed(() => {
	const items = props.options.map(option => ({
		label: option.label,
		value: toKey(option.value),
		disabled: option.disabled ?? false,
		group: option.group ?? "",
	}))

	if (pinnedSelection.value.size === 0) {
		return createListCollection({ items })
	}

	const selectedItems = items.filter(item => pinnedSelection.value.has(item.value))
	const otherItems = items.filter(item => !pinnedSelection.value.has(item.value))
	return createListCollection({ items: [...selectedItems, ...otherItems] })
})

const filteredCollection = computed(() => {
	if (!isSearchable.value || !searchQuery.value.trim()) {
		return collection.value
	}
	const q = normalize(searchQuery.value)
	const filtered = collection.value.items.filter(item => normalize(item.label).includes(q))
	return createListCollection({ items: filtered })
})

const itemGroups = computed(() => {
	const groupOrder = [...new Set(props.options.map(option => option.group ?? ""))]
	return groupOrder
		.map(label => ({ label, items: filteredCollection.value.items.filter(item => item.group === label) }))
		.filter(group => group.items.length > 0)
})

const labelsInSeveralGroups = computed(() => {
	const groupsByLabel = new Map<string, Set<string>>()
	for (const option of props.options) {
		groupsByLabel.set(option.label, (groupsByLabel.get(option.label) ?? new Set()).add(option.group ?? ""))
	}
	return new Set([...groupsByLabel].filter(([, groups]) => groups.size > 1).map(([label]) => label))
})

function optionDisplayLabel(option: ListboxOption): string {
	return option.group && labelsInSeveralGroups.value.has(option.label)
		? `${option.label} (${option.group})`
		: option.label
}

function groupKeys(label: string): string[] {
	return props.options.filter(option => option.group === label && !option.disabled).map(option => toKey(option.value))
}

function isGroupSelected(label: string): boolean {
	const keys = groupKeys(label)
	return keys.length > 0 && keys.every(key => selectedValues.value.includes(key))
}

const expandedGroups = ref<Set<string>>(new Set())
const isSearching = computed(() => isSearchable.value && searchQuery.value.trim().length > 0)

function isGroupExpanded(label: string): boolean {
	return !props.collapsibleGroups || isSearching.value || expandedGroups.value.has(label)
}

function toggleGroupExpanded(label: string): void {
	const next = new Set(expandedGroups.value)
	if (next.has(label)) next.delete(label)
	else next.add(label)
	expandedGroups.value = next
}

function selectedCountInGroup(label: string): number {
	return groupKeys(label).filter(key => selectedValues.value.includes(key)).length
}

function toggleGroup(label: string): void {
	const keys = new Set(groupKeys(label))
	selectedValues.value = isGroupSelected(label)
		? selectedValues.value.filter(key => !keys.has(key))
		: [...new Set([...selectedValues.value, ...keys])]
}

const fullySelectedGroups = computed(() =>
	props.selectableGroups
		? [...new Set(props.options.map(option => option.group).filter((group): group is string => !!group))].filter(
				isGroupSelected
			)
		: []
)

const handleValueChange = (details: any) => {
	if (!isOpen.value) return

	const next: string[] = Array.isArray(details?.value) ? details.value : details?.value ? [details.value] : []

	selectedValues.value = next

	if (!props.multiple && next.length > 0) {
		isOpen.value = false
	}
}

const displayText = computed(() => {
	const count = selectedValues.value.length
	if (count === 0) return props.placeholder

	if (!props.multiple) {
		const opt = props.options.find(o => toKey(o.value) === selectedValues.value[0])
		return opt ? optionDisplayLabel(opt) : ""
	}

	const fullGroups = new Set(fullySelectedGroups.value)
	const labels = [
		...fullySelectedGroups.value,
		...selectedValues.value
			.map(key => props.options.find(o => toKey(o.value) === key))
			.filter(option => !option?.group || !fullGroups.has(option.group))
			.map(option => (option ? optionDisplayLabel(option) : undefined))
			.filter((l): l is string => !!l),
	]

	if (labels.length === 0) return ""

	const fullText = labels.join(", ")

	if (!availableWidth.value || measureTextWidth(fullText) <= availableWidth.value) {
		return fullText
	}

	for (let n = labels.length - 1; n >= 1; n--) {
		const candidate = `${labels.slice(0, n).join(", ")} +${labels.length - n}`
		if (measureTextWidth(candidate) <= availableWidth.value) {
			return candidate
		}
	}

	return `+${labels.length}`
})

const clearSelection = async () => {
	updateModelValue(props.multiple ? [] : null)

	await nextTick()

	listboxResetKey.value++

	searchQuery.value = ""

	if (!props.multiple) isOpen.value = false
}

watch(isOpen, newValue => {
	if (newValue) {
		pinnedSelection.value = new Set(selectedValues.value)
	} else {
		searchQuery.value = ""
	}
})

watch(
	() => props.modelValue,
	() => {
		if (isInternalModelValueUpdate) {
			isInternalModelValueUpdate = false
			return
		}
		listboxResetKey.value++
	}
)
</script>

<style scoped>
.ark-listbox-dropdown {
	position: relative;
	width: 100%;

	.label {
		display: block;
		margin-bottom: var(--spacing-xxs);
		font-weight: var(--font-weight-regular);
		color: var(--neutral-600);
	}

	.trigger {
		width: 100%;
		height: var(--space-30);
		padding: 0 var(--spacing-sm) 0 var(--space-10);
		border: var(--border-width-thin) solid var(--neutral-400);
		border-radius: var(--border-radius-base);
		background-color: var(--neutral-0);
		color: var(--neutral-700);
		font-weight: var(--font-weight-regular);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;
		transition: border-color 0.2s ease;

		&:hover:not(:disabled) {
			border-color: var(--neutral-600);
		}

		&:focus,
		&:focus-visible {
			outline: none;
			border-color: var(--neutral-600);
			box-shadow: var(--shadow-sm);
		}

		&:disabled {
			background-color: var(--neutral-100);
			color: var(--neutral-200);
			cursor: not-allowed;
			opacity: 0.6;
		}

		&.has-selection {
			border-color: var(--neutral-400);
			color: var(--neutral-900);
		}

		&.is-invalid {
			border-color: var(--accent-warning);
		}

		.display-text {
			flex: 1;
			text-align: left;
			white-space: nowrap;
			overflow-x: auto;
			scrollbar-width: none;
			color: inherit;
			font-size: var(--font-size-regular);
		}

		.trigger-icons {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
		}

		.clear-button {
			display: flex;
			align-items: center;
			justify-content: center;
			width: var(--spacing-md);
			height: var(--spacing-md);
			border: none;
			background: var(--neutral-200);
			border-radius: 50%;
			cursor: pointer;
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			line-height: 1;
			transition: background-color 0.2s ease;

			&:hover:not(:disabled) {
				background: var(--neutral-400);
			}
		}

		.chevron {
			width: 6px;
			height: 6px;
			border-right: 1px solid var(--neutral-600);
			border-bottom: 1px solid var(--neutral-600);
			transform: rotate(45deg);
			margin-top: -3px;
			margin-right: var(--spacing-xxs);
			transition: transform 0.2s ease;

			&.open {
				transform: rotate(225deg);
				margin-top: 3px;
			}
		}
	}

	.error-message {
		margin-top: var(--spacing-xxs);
		color: var(--accent-warning);
		font-size: var(--font-size-sm);
	}
}

.popover-content {
	width: calc(var(--width-select-container) - var(--spacing-sm));

	&.same-width {
		width: 100%;
		min-width: var(--width-small-input);
	}
	max-height: 100%;
	font-weight: var(--font-weight-regular);
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);
	box-shadow: var(--shadow-sm);
	overflow-y: auto;
	z-index: 1000;
	margin-top: var(--spacing-xxs);
	margin-left: 0px;
	font-size: var(--font-size-regular);

	.search-container {
		padding: var(--spacing-xs);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
	}

	.search-input {
		width: 100%;
		padding: var(--spacing-xs) var(--spacing-sm);
		border: none;
		border-radius: var(--border-radius-sm);
		font-size: var(--font-size-regular);
		outline: none;
	}

	.listbox {
		max-height: var(--max-height-animation);
		overflow-y: auto;
		padding: var(--spacing-xxs);

		.no-options {
			padding: var(--spacing-sm);
			text-align: center;
			color: var(--neutral-200);
			font-style: italic;
		}

		.item-group + .item-group {
			&::before {
				content: "";
				display: block;
				margin: var(--spacing-xs) var(--space-6) var(--spacing-sm);
				border-top: var(--border-width-thin) solid var(--neutral-100);
			}
		}

		.item-group-label {
			padding: var(--spacing-xs) var(--space-6) var(--spacing-sm);
			color: var(--neutral-600);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;

			.group-toggle {
				display: flex;
				align-items: flex-start;
				gap: var(--spacing-sm);

				.group-checkbox {
					display: flex;
					justify-content: center;
					flex-shrink: 0;
					width: var(--size-icon-md);
					height: 1lh;
					align-items: center;
					padding-right: var(--spacing-xs);
				}

				.group-name {
					all: unset;
					flex: 1;
					display: flex;
					align-items: flex-start;
					gap: var(--spacing-sm);
					cursor: pointer;

					&:hover {
						color: var(--neutral-900);
					}

					.group-chevron {
						flex-shrink: 0;
						margin-top: calc((1lh - 10px) / 2);
						transition: transform 0.2s ease;

						&.expanded {
							transform: rotate(90deg);
						}
					}

					.group-count {
						color: var(--neutral-900);
					}
				}
			}
		}

		.listbox-item {
			box-sizing: border-box;
			padding: var(--spacing-xs) var(--space-6);
			min-height: var(--space-30);
			cursor: pointer;
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			border-radius: var(--border-radius-base);
			border: var(--spacing-xxs) solid var(--neutral-0);
			transition: background-color 0.15s ease;

			&:hover,
			&[data-highlighted] {
				background-color: var(--neutral-100);
			}

			&[data-selected] {
				background-color: var(--neutral-50);
				color: var(--neutral-900);

				.item-indicator .checkbox {
					opacity: 1;
					color: var(--neutral-700);
					font-weight: var(--font-weight-bold);
				}
			}

			&[data-disabled] {
				opacity: 0.5;
				cursor: not-allowed;
			}

			.item-indicator {
				display: flex;
				align-items: center;
				justify-content: center;
				width: var(--size-icon-md);
				height: var(--size-icon-md);
			}

			.item-text {
				flex: 1;
				color: inherit;
			}

			&:focus-visible {
				outline: 2px solid var(--primary-green);
				outline-offset: -2px;
			}
		}
	}
}

.popover-content {
	animation: slideDown 0.2s ease-out;
}

.popover-content[data-placement^="top"] {
	margin-bottom: var(--spacing-xxs);
}

.popover-content[data-placement^="bottom"] {
	margin-top: var(--spacing-xxs);
}

@keyframes slideDown {
	from {
		opacity: 0;
		transform: translateY(-8px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
